/**
 * Sidebar component - Tool library (Parametric Animation Refactor)
 *
 * Displays draggable movement tools in a flat list format:
 * - Puppet tools: Body part movement tools from the active puppet
 * - Control tools: Global timeline flow controls (Wait, Stop) - shared across all puppets
 * - Groups: Reusable tool groups created from timeline selections
 *
 * Features:
 * - Displays tools owned by the active puppet
 * - Displays global control tools separately
 * - Displays tool groups in collapsible section (Task Group 6)
 * - Click-to-preview tool functionality
 * - Shows puppet name and tool count in footer
 * - Handles empty state when no active puppet
 */

import { useState } from 'react';
import { usePuppetStore } from '../stores/puppetStore';
import { useToolStore } from '../stores/toolStore';
import { SidebarToolItem } from './sidebar/SidebarToolItem';
import { SidebarGroupsSection } from './sidebar/SidebarGroupsSection';
import { CollapsibleSection } from './sidebar/CollapsibleSection';

export function Sidebar() {
  // Get tools from active puppet via puppetStore
  const puppetTools = usePuppetStore((state) => state.getActivePuppetTools());
  const activePuppet = usePuppetStore((state) => state.getActivePuppetData());

  // Get global control tools from toolStore
  const controlTools = useToolStore((state) => state.controlTools);

  // Track which tool is being previewed
  const [previewingToolId, setPreviewingToolId] = useState<string | null>(null);

  /**
   * Handle tool preview
   * Clicking a tool toggles its preview in 3D (without adding to timeline)
   */
  const handleToolPreview = (toolId: string) => {
    if (previewingToolId === toolId) {
      // Second click - stop preview
      setPreviewingToolId(null);
    } else {
      // First click - start preview
      setPreviewingToolId(toolId);
    }
  };

  // Total tool count (puppet tools + control tools)
  const totalToolCount = puppetTools.length + controlTools.length;

  // Format footer text with tool count and puppet name
  const footerText = activePuppet
    ? `${totalToolCount} tools - ${activePuppet.name}`
    : 'No active puppet';

  return (
    <div className="h-full flex flex-col panel-bg">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b panel-border">
        <h2
          className="text-lg font-semibold text-dark-text"
          style={{ fontSize: 'var(--font-size-lg)' }}
        >
          Tools
        </h2>
        <p
          className="text-xs text-dark-text-muted mt-1"
          style={{ fontSize: 'var(--font-size-xs)' }}
        >
          Drag tools to timeline or click to preview
        </p>
      </div>

      {/* Tool List */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Puppet Tools Section */}
        {puppetTools.length > 0 && (
          <CollapsibleSection title="Movement" count={puppetTools.length}>
            <div className="space-y-0">
              {puppetTools.map((tool) => (
                <SidebarToolItem
                  key={tool.id}
                  tool={tool}
                  onClick={handleToolPreview}
                  isPreviewing={previewingToolId === tool.id}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Control Tools Section - Global, always shown */}
        {controlTools.length > 0 && (
          <CollapsibleSection title="Control" count={controlTools.length}>
            <div className="space-y-0">
              {controlTools.map((tool) => (
                <SidebarToolItem
                  key={tool.id}
                  tool={tool}
                  onClick={handleToolPreview}
                  isPreviewing={previewingToolId === tool.id}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Groups Section - Task Group 6 */}
        <SidebarGroupsSection />

        {/* Empty State */}
        {puppetTools.length === 0 && controlTools.length === 0 && (
          <div className="text-center text-dark-text-muted py-8">
            <p className="text-sm">No tools available</p>
            <p className="text-xs mt-1">
              {activePuppet
                ? 'Add tools via the configurator'
                : 'Select a puppet to view tools'}
            </p>
          </div>
        )}
      </div>

      {/* Footer info */}
      <div className="flex-shrink-0 border-t panel-border p-3">
        <div className="text-xs text-dark-text-muted">{footerText}</div>
      </div>
    </div>
  );
}
